import React, { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { clearAllAuthData } from "../utils/clearAuth";
import { isDemoMode, deactivateDemo } from "../demo/demoMode";
import { demoUser, demoSession } from "../demo/demoData";
type SupabaseSessionResult = {
  data: {
    session: Session | null;
  };
  error: Error | null;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithGoogle: (flow?: 'merchant' | 'provider') => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Demo mode: skip real auth, use mock user/session
    if (isDemoMode()) {
      setUser(demoUser as unknown as User);
      setSession(demoSession as unknown as Session);
      setLoading(false);
      return;
    }

    const initializeAuth = async () => {
      try {
        const {
          data: { session: existingSession },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("Error getting session:", error);
          setSession(null);
          setUser(null);
        } else if (existingSession) {
          setSession(existingSession);
          setUser(existingSession.user);
        } else {
          setSession(null);
          setUser(null);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        setSession(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    // Set up auth state change listener first
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: string, session: Session | null) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Handle successful sign in
      if (event === "SIGNED_IN" && session?.user) {
        // Create or update user profile in our database
        try {
          // createOrUpdateUserProfile(session.user);
        } catch (error) {
          console.error("Error creating/updating user profile:", error);
        }
      }
    });

    // Initialize auth - this will trigger the auth state change listener
    initializeAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const createOrUpdateUserProfile = async (user: User) => {
    try {
      // First, check if merchant exists in our merchants table (represents shops)
      const { data: existingUser, error: fetchError } = await supabase
        .from("merchants")
        .select("*")
        .eq("email", user.email)
        .single();

      if (fetchError && fetchError.code !== "PGRST116") {
        throw fetchError;
      }

      if (!existingUser) {
        // User doesn't exist, will be handled in onboarding flow
      }
    } catch (error) {
      console.error("Error in createOrUpdateUserProfile:", error);
    }
  };

  const signInWithGoogle = async (flow?: 'merchant' | 'provider') => {
    try {
      // Store flow in localStorage for post-auth redirect decision
      try {
        localStorage.setItem('flowstock.auth.flow', flow || 'merchant');
      } catch (e) {
        console.warn('Could not store flow in localStorage:', e);
      }

      // Use clean callback URL (no query params) so it matches Supabase allowed list exactly
      const redirectUrl = `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
        },
      });
      if (error) throw error;
    } catch (error: any) {
      console.error("Error signing in with Google:", error.message);
      throw error;
    }
  };

  const signOut = async () => {
    if (isDemoMode()) {
      deactivateDemo();
      return;
    }
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Supabase signOut error:", error);
      }
      setUser(null);
      setSession(null);
      window.location.replace("/login");
    } catch (error: any) {
      console.error("Error signing out:", error.message);
      clearAllAuthData();
      setUser(null);
      setSession(null);
      window.location.replace("/login");
    }
  };

  const value = {
    user,
    session,
    loading,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
